import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApplyWrapper,
  CharacterCount,
  Container,
  ErrorMessage,
  HelperRow,
  LanguageGroup,
  LanguageLabel,
  StyledTextArea,
  Title,
  TitleContainer,
  Wrapper,
} from './AdditionalCartDetailsModal.styled';
import Button from 'components/Button/Button';
import { Modal } from 'components/Modals/Modal/Modal';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { IAdditionalCartDetails } from 'types/Company.types';
import {
  ADDITIONAL_CART_DETAILS_MAX_LENGTH,
  SUPPORTED_LANGUAGES,
} from './constants';

interface IAdditionalCartDetailsModalProps {
  onCancel: () => void;
  onSave: (details: IAdditionalCartDetails | null) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  additionalCartDetails?: IAdditionalCartDetails | null;
}

export const AdditionalCartDetailsModal = ({
  isOpen,
  setIsOpen,
  onCancel,
  onSave,
  additionalCartDetails,
}: IAdditionalCartDetailsModalProps) => {
  const { t } = useTranslation();
  const { isSmallTablet } = useBreakpointFlag();
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    SUPPORTED_LANGUAGES.forEach(({ code }) => {
      initial[code] = additionalCartDetails?.[code] ?? '';
    });
    setValues(initial);
  }, [additionalCartDetails, isOpen]);

  const handleChange = (code: string, value: string) => {
    setValues((prev) => ({ ...prev, [code]: value }));
  };

  const isAnyInvalid = SUPPORTED_LANGUAGES.some(
    ({ code }) =>
      (values[code]?.length ?? 0) > ADDITIONAL_CART_DETAILS_MAX_LENGTH
  );

  const handleSave = () => {
    const result: IAdditionalCartDetails = {};
    SUPPORTED_LANGUAGES.forEach(({ code }) => {
      const trimmed = values[code]?.trim();
      if (trimmed) {
        result[code] = trimmed;
      }
    });
    onSave(Object.keys(result).length > 0 ? result : null);
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      modalStyle={{
        position: 'fixed',
        width: isSmallTablet ? '90%' : '700rem',
        minWidth: isSmallTablet ? 'auto' : 'auto',
        margin: 'auto',
      }}
      level="SECOND"
    >
      <Container>
        <TitleContainer>
          <Title>{t('Additional cart details')}</Title>
        </TitleContainer>
        <Wrapper>
          {SUPPORTED_LANGUAGES.map(({ code, name }) => {
            const value = values[code] ?? '';
            const length = value.length;
            const hasError = length > ADDITIONAL_CART_DETAILS_MAX_LENGTH;
            return (
              <LanguageGroup key={code}>
                <LanguageLabel>{name}</LanguageLabel>
                <StyledTextArea
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleChange(code, e.target.value)
                  }
                  hasError={hasError}
                  maxLength={ADDITIONAL_CART_DETAILS_MAX_LENGTH}
                />
                <HelperRow>
                  {hasError ? (
                    <ErrorMessage>
                      {t('Maximum {{max}} characters allowed', {
                        max: ADDITIONAL_CART_DETAILS_MAX_LENGTH,
                      })}
                    </ErrorMessage>
                  ) : (
                    <span />
                  )}
                  <CharacterCount hasError={hasError}>
                    {length}/{ADDITIONAL_CART_DETAILS_MAX_LENGTH}
                  </CharacterCount>
                </HelperRow>
              </LanguageGroup>
            );
          })}
        </Wrapper>
        <ApplyWrapper>
          <Button
            onClick={onCancel}
            secondary
            width="200rem"
            label={t('Cancel')}
          />
          <Button
            disabled={isAnyInvalid}
            onClick={handleSave}
            primary
            width="200rem"
            label={t('Apply all')}
          />
        </ApplyWrapper>
      </Container>
    </Modal>
  );
};
